import { buildTextParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import { buildNonApplicableFamilyTargetResult } from '../../revit/parameterAdditionResultPolicy';
import { mapRevitOperationSnapshotToProgressModel } from '../../../ui/progress';
import {
  buildAdditionalCategoryBatchGroups,
  buildAdditionalCategoryFamilyUndoPlan,
  collectAdditionalCategoryTargets,
  type EvaluatedAdditionalCategoryTarget,
  selectAdditionalCategoryFamilyParameterTargets,
} from './additionalCategoryPlan';
import { fetchAdditionalCategoryDefinitionSnapshot } from './additionalCategoryDefinitionSnapshot';
import { notifyParameterAddition } from './feedback';
import { projectChangedResult } from './operationIdentity';
import { type ParameterAdditionOptions, type ParameterAdditionResult } from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';

export async function runAdditionalCategoryFamilyAddition(
  context:
    & Pick<
      ParameterAdditionCore,
      | 'additionalCategoriesConfig'
      | 'additionalCategoryBatchOperation'
      | 'didProjectChange'
      | 'menuConfig'
      | 'setAdditionalCategoryParameterUndoPlan'
      | 'setParameterProgress'
      | 'showInfo'
      | 'showWarning'
    >
    & Pick<ParameterMutationActions, 'addToObjectTypeFamilies'>,
  requestedProjectKey: string,
  options: ParameterAdditionOptions,
): Promise<ParameterAdditionResult> {
  const {
    addToObjectTypeFamilies,
    additionalCategoriesConfig,
    additionalCategoryBatchOperation,
    didProjectChange,
    menuConfig,
    setAdditionalCategoryParameterUndoPlan,
    setParameterProgress,
    showInfo,
    showWarning,
  } = context;
  const objectTypeParameterName = menuConfig.objectTypeValue.trim();

  const targets = collectAdditionalCategoryTargets(
    additionalCategoriesConfig,
  );

  // Plan from authoritative family-document evidence: the broad family
  // snapshot's instance sweep samples placed elements, so a project-bound
  // parameter looks present on families that never received it. The
  // definition-only shape replaces swept rows with family-document
  // definitions, which is the evidence a family-destination write must use.
  // The discriminator intent follows the additional-categories page's own
  // mode: that is the route Quick Fill used to write the classification
  // values this page's matching must read back.
  const familySnapshotResult = await fetchAdditionalCategoryDefinitionSnapshot({
    additionalCategoriesConfig,
    objectTypeParameterName,
    discriminatorDestination:
      additionalCategoriesConfig.parameterDestination === 'project'
        ? 'project'
        : 'family',
    discriminatorBindingKind:
      additionalCategoriesConfig.parameterBindingKind === 'type'
        ? 'type'
        : 'instance',
  });

  if (didProjectChange(requestedProjectKey)) {
    const result = projectChangedResult();
    notifyParameterAddition(
      options,
      showWarning,
      result.error ?? 'The active Revit project changed.',
    );
    return result;
  }

  const additionalCategoryBindingKind =
    additionalCategoriesConfig.parameterBindingKind ?? 'instance';
  const familyTargetPlan = selectAdditionalCategoryFamilyParameterTargets(
    targets,
    familySnapshotResult.families,
    additionalCategoryBindingKind,
    objectTypeParameterName,
    familySnapshotResult.available,
    additionalCategoryBindingKind,
  );

  // Targets delegated to Revit via broad object-type targeting were queued
  // (and the host resolves their families itself), so only targets that were
  // neither matched nor delegated may be reported as skipped.
  const skippedTargetLabels = familyTargetPlan.targetEvaluations
    .filter((target) =>
      target.matchingFamilyCount === 0 &&
      !familyTargetPlan.batchTargets.includes(target)
    )
    .map((target) => target.label);

  if (familyTargetPlan.batchTargets.length === 0) {
    if (skippedTargetLabels.length > 0) {
      const skippedParameterNames = familyTargetPlan.targetEvaluations
        .filter((target) => target.matchingFamilyCount === 0)
        .flatMap((target) => target.parameterNames);
      notifyParameterAddition(
        options,
        showInfo,
        `Skipped ${skippedTargetLabels.join(', ')} because the active model has no matching editable families.`,
      );
      return buildNonApplicableFamilyTargetResult(
        skippedParameterNames,
      );
    }

    notifyParameterAddition(
      options,
      showInfo,
      'All additional-category parameters are already available. No new parameters were added.',
    );
    return {
      success: true,
      addedCount: 0,
      existingCount: targets.reduce(
        (sum, target) => sum + target.parameterNames.length,
        0,
      ),
      queued: false,
      requestedCount: targets.reduce(
        (sum, target) => sum + target.parameterNames.length,
        0,
      ),
    };
  }

  const batchGroups = buildAdditionalCategoryBatchGroups(
    familyTargetPlan.batchTargets,
  );

  const results: ParameterAdditionResult[] = [];

  const totalFamilyTargets = batchGroups.reduce(
    (sum, group) => sum + (group.targetFamilyIds?.length ?? 0),
    0,
  );

  const familyTargetScopeLabel = totalFamilyTargets > 0
    ? `${totalFamilyTargets} family definition target${totalFamilyTargets === 1 ? '' : 's'}`
    : 'Revit-resolved family definition targets';

  await additionalCategoryBatchOperation.run({
    title: 'Additional-category parameter batch',
    message: `Processing the complete additional-category family scope.`,
    totalCount: batchGroups.length,
    run: async (helpers) =>
      helpers.runSequential({
        items: batchGroups,
        runItem: async (group, index) => {
          const completedGroups = index;
          setParameterProgress({
            title: 'Adding category parameters',
            message: 'Revit is updating additional-category family definitions',
            detail: `Processing complete family scope (${
              completedGroups + 1
            } of ${batchGroups.length} groups, ${familyTargetScopeLabel}).`,
            status: 'running',
            percent: batchGroups.length > 0
              ? Math.round(
                (completedGroups / batchGroups.length) * 100,
              )
              : null,
            locked: true,
          });
          const result = await addToObjectTypeFamilies(
            group.parameterNames,
            group.targetObjectTypes,
            additionalCategoriesConfig.parameterBindingKind ??
              'instance',
            {
              targetFamilyIds: group.targetFamilyIds,
              objectTypeParameterName,
              expectedFamilyCount: group.targetFamilyIds?.length,
              parameterTypeHints: buildTextParameterTypeHints(
                group.parameterNames,
              ),
              onProgress: (progress) => {
                const childPercent = typeof progress.percent === 'number'
                  ? Math.max(0, Math.min(100, progress.percent))
                  : progress.total && progress.total > 0 &&
                      typeof progress.processed === 'number'
                  ? Math.max(
                    0,
                    Math.min(
                      100,
                      (progress.processed / progress.total) * 100,
                    ),
                  )
                  : 0;
                const aggregatePercent = batchGroups.length > 0
                  ? Math.round(
                    ((completedGroups + (childPercent / 100)) /
                      batchGroups.length) * 100,
                  )
                  : null;
                const hostModel = mapRevitOperationSnapshotToProgressModel(progress, {
                  title: 'Adding category parameters',
                  message: 'Revit is updating additional-category family definitions',
                  completeMessage: 'Additional-category family group completed',
                  errorMessage: 'Additional-category family setup failed',
                  locked: !progress.failed,
                });
                const groupLabel = `${group.labels.join(', ')} group ${completedGroups + 1} of ${batchGroups.length}`;
                setParameterProgress({
                  ...hostModel,
                  title: 'Adding category parameters',
                  detail: hostModel.detail
                    ? `${groupLabel}: ${hostModel.detail}`
                    : `Processing ${groupLabel} across the complete family scope.`,
                  status: progress.failed ? 'error' : 'running',
                  percent: aggregatePercent,
                  locked: !progress.failed,
                });
              },
            },
          );
          setParameterProgress({
            title: 'Adding category parameters',
            message: 'Revit is updating additional-category family definitions',
            detail: `Processed ${
              completedGroups + 1
            } of ${batchGroups.length} additional-category groups across the complete family scope.`,
            status: 'running',
            percent: Math.round(
              ((completedGroups + 1) / batchGroups.length) * 100,
            ),
            locked: true,
          });
          results.push(result);
          return { group, result };
        },
        toItemResult: ({ group, result }) => ({
          label: `${group.labels.join(', ')} (${group.parameterNames.length} parameter${
            group.parameterNames.length === 1 ? '' : 's'
          })`,
          detail: `${group.targetObjectTypes.join(', ')}`,
          status: result.success ? 'success' : 'failed',
          error: result.error,
        }),
      }),
  });

  if (didProjectChange(requestedProjectKey)) {
    const result = projectChangedResult();
    notifyParameterAddition(
      options,
      showWarning,
      result.error ?? 'The active Revit project changed.',
    );
    return result;
  }

  const failed = results.find((result) => !result.success);

  const totalRequested = targets.reduce(
    (sum, target) => sum + target.parameterNames.length,
    0,
  );

  if (failed) {
    notifyParameterAddition(
      options,
      showWarning,
      failed.error ||
        'Some additional-category parameters could not be queued.',
    );
    return failed;
  }

  const totalResolvedFamilyTargets = results.reduce(
    (sum, result) =>
      sum +
      (result.resolvedFamilyTargetCount ?? result.familyCount ?? 0),
    0,
  );

  const totalModifiedFamilyTargets = results.reduce(
    (sum, result) => sum + (result.modifiedFamilyCount ?? 0),
    0,
  );

  const totalUnmodifiedFamilyTargets = results.reduce(
    (sum, result) => sum + (result.unmodifiedFamilyTargetCount ?? 0),
    0,
  );

  const familyTargetSummary = totalResolvedFamilyTargets > 0
    ? ` Changed and loaded ${totalModifiedFamilyTargets} of ${totalResolvedFamilyTargets} family definition target${
      totalResolvedFamilyTargets === 1 ? '' : 's'
    }.${totalUnmodifiedFamilyTargets > 0 ? ` ${totalUnmodifiedFamilyTargets} needed no reload.` : ''}`
    : '';

  const additionalCategoryUndoPlan = familyTargetPlan.usesBroadObjectTypeTargeting
    ? null
    : buildAdditionalCategoryFamilyUndoPlan(
      familyTargetPlan.batchTargets.filter(
        (target): target is EvaluatedAdditionalCategoryTarget => Array.isArray(target.matchingFamilies),
      ),
    );

  const totalQueued = batchGroups.reduce(
    (sum, group) => sum + group.parameterNames.length,
    0,
  );

  notifyParameterAddition(
    options,
    showInfo,
    skippedTargetLabels.length > 0
      ? `Queued ${totalQueued} additional-category parameter mapping${
        totalQueued === 1 ? '' : 's'
      } for matching component families.${familyTargetSummary} Skipped ${
        skippedTargetLabels.join(', ')
      } because no editable matching families were found.`
      : `Queued ${totalRequested} additional-category parameter mappings for matching component families.${familyTargetSummary}`,
  );

  if (didProjectChange(requestedProjectKey)) {
    const result = projectChangedResult();
    notifyParameterAddition(
      options,
      showWarning,
      result.error ?? 'The active Revit project changed.',
    );
    return result;
  }

  if (additionalCategoryUndoPlan) {
    setAdditionalCategoryParameterUndoPlan({
      ...additionalCategoryUndoPlan,
      projectKey: requestedProjectKey,
    });
  }

  if (skippedTargetLabels.length > 0) {
    return {
      success: true,
      addedCount: 0,
      existingCount: 0,
      failedCount: 0,
      conflictingCount: 0,
      queued: true,
      requestedCount: totalRequested,
      familyCount: totalResolvedFamilyTargets,
      modifiedFamilyCount: totalModifiedFamilyTargets,
      resolvedFamilyTargetCount: totalResolvedFamilyTargets,
      unmodifiedFamilyTargetCount: totalUnmodifiedFamilyTargets,
      undoAvailable: additionalCategoryUndoPlan !== null,
    };
  }

  return {
    success: true,
    addedCount: 0,
    queued: true,
    requestedCount: totalRequested,
    familyCount: totalResolvedFamilyTargets,
    modifiedFamilyCount: totalModifiedFamilyTargets,
    resolvedFamilyTargetCount: totalResolvedFamilyTargets,
    unmodifiedFamilyTargetCount: totalUnmodifiedFamilyTargets,
    undoAvailable: additionalCategoryUndoPlan !== null,
  };
}
